import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiService } from '../services/ApiService';
import type { User, AuthState, LoginCredentials, SignupCredentials } from '../types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (credentials: SignupCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User | null };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true };
    case 'LOGIN_SUCCESS':
      return {
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        user: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'LOGOUT':
      return {
        user: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_USER':
      return {
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
      };
    default:
      return state;
  }
};

// Authentication service using real backend
const authService = {
  async login(credentials: LoginCredentials): Promise<User> {
    const response = await apiService.login({
      email: credentials.email,
      password: credentials.password
    });
    
    // Convert backend user format to frontend format
    const user: User = {
      id: response.user.id.toString(),
      email: response.user.email,
      username: response.user.username,
      passwordHash: '', // Not needed on frontend
      fullName: response.user.full_name
    };
    
    localStorage.setItem('auth_user', JSON.stringify(user));
    return user;
  },

  async signup(credentials: SignupCredentials): Promise<User> {
    const response = await apiService.register({
      email: credentials.email,
      password: credentials.password,
      full_name: credentials.fullName,
      username: credentials.username
    });
    
    // After registration, automatically log in
    return this.login({
      email: credentials.email,
      password: credentials.password
    });
  },

  logout(): void {
    apiService.logout();
    localStorage.removeItem('auth_user');
  },

  getCurrentUser(): User | null {
    const stored = localStorage.getItem('auth_user');
    const token = localStorage.getItem('auth_token');
    
    // If no token, clear user data
    if (!token && stored) {
      localStorage.removeItem('auth_user');
      return null;
    }
    
    return stored ? JSON.parse(stored) : null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    isAuthenticated: false,
    loading: true,
  });

  useEffect(() => {
    // Check for existing session on app start
    const user = authService.getCurrentUser();
    dispatch({ type: 'SET_USER', payload: user });
  }, []);

  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const user = await authService.login(credentials);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const signup = async (credentials: SignupCredentials) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const user = await authService.signup(credentials);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};